/**
 * IAScoreSummary Component
 * Right panel showing IA score, breakdown, and quick actions
 * Design matches PIScoreSummary for consistency
 */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle2, 
  Circle, 
  AlertCircle, 
  Plus, 
  RotateCcw,
  Printer,
  BookOpen,
  Loader2
} from 'lucide-react';
import { type IAScore, type IARequirements, type IAAttestation, getStatusLabel } from '@/lib/ia-calculator-utils';

interface IAScoreSummaryProps {
  score: IAScore | null;
  requirements: IARequirements | null;
  iaWeight: number;
  attestations: IAAttestation[];
  onReset: () => void;
  onAddActivity: () => void;
  isResetting?: boolean;
  /** When true (e.g. locked year), mutating quick actions are disabled. */
  disabled?: boolean;
}

export function IAScoreSummary({
  score,
  requirements,
  iaWeight,
  attestations,
  onReset,
  onAddActivity,
  isResetting = false,
  disabled = false,
}: IAScoreSummaryProps) {
  const totalScore = score?.totalScore || 0;
  const maxScore = score?.maxScore || 40;
  const requiredCount = requirements?.activitiesRequired || 2;
  const completedCount = score?.completedCount || 0;
  const isComplete = completedCount >= requiredCount;
  const effectiveWeight = iaWeight ?? 15;
  const weightedScore = (totalScore / maxScore) * effectiveWeight;
  const scorePercent = (totalScore / maxScore) * 100;

  return (
    <div className="space-y-4 sticky top-4">
      {/* Total IA Score Card - Matches PI design */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-6">
            {/* Circular progress indicator - Matches PI size */}
            <div className="relative w-32 h-32 flex-shrink-0">
              <svg className="w-32 h-32 -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  stroke="#E5E7EB"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  className="stroke-primary"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(scorePercent / 100) * 264} 264`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900 leading-none">{totalScore}</span>
                <span className="text-xs text-gray-400 mt-0.5">/ {maxScore}</span>
              </div>
            </div>
            {/* Text content */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">Total Improvement Activities (IA) Score</h3>
            {isComplete && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Complete
              </Badge>
            )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
                {completedCount} of {requiredCount} activities completed
            </p>
          </div>
              </div>
            </div>
        {/* Dark blue footer section - Matches PI */}
        <div className="bg-primary px-6 py-4">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-white/80 uppercase tracking-wide">Weighted Category Score</span>
            <span className="text-base font-bold text-white">{weightedScore.toFixed(2)} pts</span>
              </div>
            </div>
          </div>

      {/* Activity Breakdown Card - IA-specific content */}
      <Card>
        <CardContent className="p-0">
          {/* Header with divider - Matches PI */}
          <div className="px-5 pt-5 pb-3 border-b" style={{ borderColor: '#F0F2F5' }}>
            <h3 className="text-base font-semibold text-gray-900">Activity Breakdown</h3>
          </div>
          
          {/* Activity list */}
          <div className="px-5 py-3">
          {attestations.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No activities selected yet
            </p>
          ) : (
            <div className="space-y-2">
                {attestations.map((att, index) => {
                const statusIcon = att.is_valid ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : att.status === 'in_progress' ? (
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-300" />
                );
                
                const points = att.is_valid ? (requirements?.pointsPerActivity || 20) : 0;
                  const isLast = index === attestations.length - 1;
                
                return (
                  <div 
                    key={att.ia_activity_id}
                      className={`flex items-center justify-between py-2.5 ${!isLast ? 'border-b' : ''}`}
                      style={{ borderColor: '#F0F2F5' }}
                  >
                      <div className="flex items-center gap-3 min-w-0">
                      {statusIcon}
                      <div className="min-w-0">
                          <span className="font-medium text-sm text-gray-900">
                            {att.ia_activity_id}
                          </span>
                          <span className="text-xs text-gray-500 block">
                          {getStatusLabel(att.status)}
                        </span>
                      </div>
                    </div>
                    <div className={`
                      text-sm font-medium
                      ${att.is_valid ? 'text-emerald-600' : 'text-gray-400'}
                    `}>
                      {points} pts
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Total */}
          {attestations.length > 0 && (
            <>
              <Separator className="my-3" />
              <div className="flex items-center justify-between font-medium">
                <span className="text-gray-700">Total Score</span>
                <span className="text-gray-900">{totalScore} / {maxScore}</span>
              </div>
            </>
          )}
          </div>
          
          {/* Footer hint - Matches PI */}
          <div className="px-5 py-3 border-t bg-slate-50 rounded-b-lg" style={{ borderColor: '#E5E7EB' }}>
            <p className="text-xs text-gray-500">
              Shows points earned for each attested activity.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions Card - Matches PI design */}
      <Card>
        <CardContent className="p-0">
          {/* Header with divider */}
          <div className="px-5 pt-5 pb-3 border-b" style={{ borderColor: '#F0F2F5' }}>
            <h3 className="text-base font-semibold text-gray-900">Quick Actions</h3>
          </div>
          
          {/* Action rows */}
          <div className="px-3 py-2">
            <button
            onClick={onAddActivity}
              disabled={disabled}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Add Activity</span>
            </button>
            
            <div className="mx-3 border-t" style={{ borderColor: '#F0F2F5' }} />
            
            <button
              onClick={onReset}
              disabled={isResetting || disabled}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResetting ? (
                <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
              ) : (
                <RotateCcw className="w-5 h-5 text-gray-500" />
              )}
              <span className="text-sm font-medium text-gray-700">
                {isResetting ? 'Resetting...' : 'Reset All'}
              </span>
            </button>
            
            <div className="mx-3 border-t" style={{ borderColor: '#F0F2F5' }} />
            
            <button
              onClick={() => window.print()}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
            >
              <Printer className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Print Summary</span>
            </button>
            
            <div className="mx-3 border-t" style={{ borderColor: '#F0F2F5' }} />
            
            <button
              onClick={() => window.open('https://qpp.cms.gov/mips/improvement-activities', '_blank', 'noopener,noreferrer')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
            >
              <BookOpen className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">IA Requirements Guide</span>
            </button>
          </div>
          
          {/* Footer hint */}
          <div className="px-5 py-3 border-t bg-slate-50 rounded-b-lg" style={{ borderColor: '#E5E7EB' }}>
            <p className="text-xs text-gray-500">
              These tools apply to your IA Calculator inputs only.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
